import React, { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import Icon from '../Icon';
import api from '../../utils/api';
import ProfileModal from '../ProfileModal';

// Single source of truth for admin navigation. Flat, grouped, no nested accordions.
const NAV_GROUPS = [
  {
    items: [{ to: '/admin', label: 'Dashboard', icon: 'grid-1x2', exact: true }],
  },
  {
    label: 'Wallet',
    items: [
      { to: '/admin/add-deposit', label: 'Add Deposit', icon: 'cash-coin' },
      { to: '/admin/manage-wallet', label: 'Manage Wallet', icon: 'wallet2' },
    ],
  },
  {
    label: 'Comms',
    items: [
      { to: '/admin/sms', label: 'SMS', icon: 'chat-dots' },
      { to: '/admin/sms-history', label: 'SMS History', icon: 'clock-history' },
      { to: '/admin/email', label: 'Email', icon: 'envelope' },
      { to: '/admin/email-history', label: 'Email History', icon: 'envelope-paper' },
    ],
  },
  {
    label: 'Operations',
    items: [
      { to: '/admin/mpesa-reconciliation', label: 'M-Pesa Recon', icon: 'arrow-left-right' },
      { to: '/admin/mpesa-pull', label: 'M-Pesa Pull', icon: 'database' },
      { to: '/admin/agency-trials', label: 'Agency Management', icon: 'building' },
      { to: '/admin/support-tickets', label: 'Support Tickets', icon: 'life-preserver', badgeKey: 'support' },
      { to: '/admin/users', label: 'Management', icon: 'people' },
      { to: '/admin/kra', label: 'KRA PRN', icon: 'receipt' },
    ],
  },
];

function AdminShell({
  user,
  title = 'Administrator Panel',
  subtitle = '',
  children,
  maxWidth = 'max-w-6xl',
}) {
  const navigate = useNavigate();
  const location = useLocation();
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [supportUnreadCount, setSupportUnreadCount] = useState(0);
  const [showProfileModal, setShowProfileModal] = useState(false);
  const [currentUser, setCurrentUser] = useState(user);

  useEffect(() => {
    setCurrentUser(user);
  }, [user]);

  // Close the drawer on navigation.
  useEffect(() => {
    setDrawerOpen(false);
  }, [location.pathname]);

  // Lock body scroll while the drawer is open (mobile).
  useEffect(() => {
    if (drawerOpen) {
      const prev = document.body.style.overflow;
      document.body.style.overflow = 'hidden';
      return () => {
        document.body.style.overflow = prev;
      };
    }
  }, [drawerOpen]);

  useEffect(() => {
    let active = true;
    const loadSupportSummary = async () => {
      try {
        const response = await api.get('/auth/support-tickets/admin-summary');
        if (!active) return;
        setSupportUnreadCount(Number(response?.data?.unreadCount || 0));
      } catch (error) {
        if (!active) return;
        setSupportUnreadCount(0);
      }
    };

    if (user?.role === 'A') {
      loadSupportSummary();
    }

    return () => {
      active = false;
    };
  }, [location.pathname, user?.role]);

  const handleLogout = () => {
    localStorage.removeItem('token');
    navigate('/login');
  };

  const getInitials = (name) => {
    if (!name) return 'A';
    return String(name)
      .split(' ')
      .map((word) => word.charAt(0))
      .join('')
      .substring(0, 2)
      .toUpperCase();
  };

  const isActive = (to, exact) => {
    if (exact) return location.pathname === to;
    return location.pathname === to || location.pathname.startsWith(`${to}/`);
  };

  const badgeFor = (key) => {
    if (key === 'support') return supportUnreadCount;
    return 0;
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-emerald-50 via-white to-green-50 font-sans">
      <header className="sticky top-0 z-40 border-b border-gray-200 bg-white/90 backdrop-blur-md shadow-sm">
        <div className={`mx-auto ${maxWidth} px-3 sm:px-6`}>
          <div className="flex h-14 items-center justify-between gap-3 sm:h-16">
            <div className="flex min-w-0 items-center gap-2.5">
              <img
                src="/logofinalrounded.png"
                alt="RecordLOGS logo"
                className="h-9 w-9 rounded-xl object-cover shadow-sm sm:h-10 sm:w-10"
              />
              <div className="min-w-0">
                <h1 className="truncate text-base font-bold leading-tight text-gray-900 sm:text-lg">
                  {title || 'RecordLOGS Admin'}
                </h1>
                {subtitle ? (
                  <p className="truncate text-[11px] leading-tight text-gray-500">{subtitle}</p>
                ) : null}
              </div>
            </div>

            <div className="flex items-center gap-2">
              <button
                onClick={() => navigate('/admin/support-tickets')}
                className="relative inline-flex h-10 w-10 items-center justify-center rounded-xl border border-gray-200 bg-white text-gray-700 shadow-sm transition hover:bg-gray-50"
                aria-label="Open support tickets"
              >
                <Icon name="bell" size="1.1rem" />
                {supportUnreadCount > 0 ? (
                  <span className="absolute -right-1.5 -top-1.5 min-w-[1.25rem] rounded-full bg-red-500 px-1.5 py-0.5 text-[10px] font-bold leading-none text-white">
                    {supportUnreadCount > 99 ? '99+' : supportUnreadCount}
                  </span>
                ) : null}
              </button>

              <button
                onClick={() => setDrawerOpen(true)}
                className="inline-flex h-10 items-center justify-center gap-2 rounded-xl border border-gray-200 bg-white px-3 text-sm font-medium text-gray-700 shadow-sm transition hover:bg-gray-50"
                aria-label="Open menu"
              >
                <Icon name="list" size="1.25rem" />
                <span className="hidden sm:inline">Menu</span>
              </button>
            </div>
          </div>
        </div>
      </header>

      {/* Unified navigation drawer */}
      <div
        className={`fixed inset-0 z-50 transition-opacity duration-200 ${
          drawerOpen ? 'visible opacity-100' : 'invisible opacity-0'
        }`}
        aria-hidden={!drawerOpen}
      >
        <div className="absolute inset-0 bg-gray-900/40 backdrop-blur-[1px]" onClick={() => setDrawerOpen(false)} />

        <aside
          className={`absolute right-0 top-0 flex h-full w-[17rem] max-w-[85vw] flex-col bg-white shadow-2xl transition-transform duration-300 ease-out ${
            drawerOpen ? 'translate-x-0' : 'translate-x-full'
          }`}
        >
          <div className="flex items-center gap-3 border-b border-gray-100 px-4 py-3.5">
            <div className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-full bg-gradient-to-br from-emerald-500 to-emerald-600 text-sm font-bold text-white">
              {getInitials(currentUser?.name)}
            </div>
            <div className="min-w-0 flex-1">
              <div className="truncate text-sm font-semibold text-gray-900">{currentUser?.name || 'Admin'}</div>
              <div className="truncate text-[11px] text-gray-500">{currentUser?.email || 'Administrator'}</div>
            </div>
            <button
              onClick={() => setDrawerOpen(false)}
              className="flex h-8 w-8 items-center justify-center rounded-lg text-gray-400 transition hover:bg-gray-100 hover:text-gray-600"
              aria-label="Close menu"
            >
              <Icon name="x-lg" size="1.1rem" />
            </button>
          </div>

          <nav className="flex-1 overflow-y-auto px-2 py-3">
            {NAV_GROUPS.map((group, gi) => (
              <div key={group.label || `g-${gi}`} className={gi > 0 ? 'mt-3' : ''}>
                {group.label ? (
                  <div className="px-3 pb-1.5 text-[10px] font-semibold uppercase tracking-[0.16em] text-gray-400">
                    {group.label}
                  </div>
                ) : null}
                <div className="space-y-0.5">
                  {group.items.map((item) => {
                    const active = isActive(item.to, item.exact);
                    const badge = item.badgeKey ? badgeFor(item.badgeKey) : 0;
                    return (
                      <button
                        key={item.to}
                        onClick={() => navigate(item.to)}
                        className={`flex w-full items-center gap-3 rounded-xl px-3 py-2 text-left text-sm transition ${
                          active
                            ? 'bg-emerald-50 font-semibold text-emerald-700'
                            : 'font-medium text-gray-700 hover:bg-gray-50'
                        }`}
                      >
                        <Icon
                          name={item.icon}
                          size="1.05rem"
                          className={active ? 'text-emerald-600' : 'text-gray-400'}
                        />
                        <span className="flex-1 truncate">{item.label}</span>
                        {badge > 0 ? (
                          <span className="min-w-[1.25rem] rounded-full bg-red-500 px-1.5 py-0.5 text-center text-[10px] font-bold leading-none text-white">
                            {badge > 99 ? '99+' : badge}
                          </span>
                        ) : null}
                        {active ? <span className="h-1.5 w-1.5 rounded-full bg-emerald-500" /> : null}
                      </button>
                    );
                  })}
                </div>
              </div>
            ))}
          </nav>

          <div className="border-t border-gray-100 p-2">
            <button
              onClick={() => {
                setShowProfileModal(true);
                setDrawerOpen(false);
              }}
              className="flex w-full items-center gap-3 rounded-xl px-3 py-2 text-sm font-medium text-gray-700 transition hover:bg-gray-50"
            >
              <Icon name="person-gear" size="1.05rem" className="text-gray-400" />
              Profile &amp; Security
            </button>
            <button
              onClick={handleLogout}
              className="flex w-full items-center gap-3 rounded-xl px-3 py-2 text-sm font-medium text-red-600 transition hover:bg-red-50"
            >
              <Icon name="box-arrow-right" size="1.05rem" />
              Sign Out
            </button>
          </div>
        </aside>
      </div>

      <main className={`relative mx-auto ${maxWidth} px-3 py-4 sm:px-6 sm:py-6`}>{children}</main>

      {showProfileModal ? (
        <ProfileModal
          user={currentUser}
          onClose={() => setShowProfileModal(false)}
          onSaved={(updatedUser) => {
            setCurrentUser(updatedUser);
            setShowProfileModal(false);
          }}
        />
      ) : null}
    </div>
  );
}

export default AdminShell;
